import React from "react";

import * as Styled from "./styles";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  FormLabel,
  Grid,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { ReturnButton } from "components/ReturnButton";
import { Controller, useForm } from "react-hook-form";
import { IDeclaration, ITreatment } from "shared/types/Declaration";

import SignatureCanvas from "react-signature-canvas";

import { IMaskInput } from "react-imask";
import { sendDeclaration } from "requests/Declaration";
import { useAuth } from "shared/providers/auth";
import toast from "react-hot-toast";
import { pdfDownload } from "requests/PdfDownload";
import { PostSubmitModal } from "../PostSubmitModal";

interface IProps {
  open: boolean;
  close: (open: boolean) => void;
  pacId: number;
  pacFantasy: string;
  onCompleteOrto?: () => void;
  emailPatient?: string;
}

const DeclarationReferrals: React.FC<IProps> = ({
  open,
  close,
  pacId,
  pacFantasy,
  onCompleteOrto,
  emailPatient,
}) => {
  const { user } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors },
    //reset,
  } = useForm<
    Omit<
      IDeclaration,
      "tratamentos" | "assinatura_paciente" | "assinatura_dentista"
    >
  >({
    defaultValues: {
      responsavel: "",
      rg: "",
      cpf: "",
      outros: "",
      observacao: "",
    },
  });
  const [loading, setLoading] = React.useState<boolean>(false);
  const [idForm, setIdForm] = React.useState<number>(0);

  const [tratamentos, setTratamentos] = React.useState<ITreatment>({
    periodontal: false,
    protetico: false,
    implantes: false,
    restaurador: false,
    cirurgico: false,
    estetico: false,
    outros: false,
  });

  const [signatureDoctor, setSignatureDoctor] = React.useState<any>();
  const [signaturePatient, setSignaturePatient] = React.useState<any>();

  const [showPostModal, setShowPostModal] = React.useState(false);

  const sigCanvasPatient = React.useRef<SignatureCanvas>(null);
  const sigCanvasDentist = React.useRef<SignatureCanvas>(null);
  const sigBoxRef = React.useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = React.useState<number>(0);

  React.useEffect(() => {
    if (!open) return;

    const measure = () => {
      if (sigBoxRef.current) {
        setCanvasWidth(sigBoxRef.current.clientWidth - 2);
      }
    };

    const timer = setTimeout(measure, 50);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const compressSignature = (canvas: HTMLCanvasElement, maxWidth = 400): string => {
    const scale = canvas.width > maxWidth ? maxWidth / canvas.width : 1;
    const resized = document.createElement("canvas");
    resized.width = canvas.width * scale;
    resized.height = canvas.height * scale;
    const ctx = resized.getContext("2d");
    if (ctx) {
      ctx.drawImage(canvas, 0, 0, resized.width, resized.height);
    }
    return resized.toDataURL("image/png");
  };

  const saveSignaturePatient = () => {
    if (sigCanvasPatient.current) {
      const trimmed = sigCanvasPatient.current.getTrimmedCanvas();
      setSignaturePatient(compressSignature(trimmed));
    }
  };

  const saveSignatureDentist = () => {
    if (sigCanvasDentist.current) {
      const trimmed = sigCanvasDentist.current.getTrimmedCanvas();
      setSignatureDoctor(compressSignature(trimmed));
    }
  };

  const clearSignaturePatient = () => {
    sigCanvasPatient.current?.clear();
    setSignaturePatient(null);
  };

  const clearSignatureDentits = () => {
    sigCanvasDentist.current?.clear();
    setSignatureDoctor(null);
  };

  const handleTratamentoChange = (key: keyof IDeclaration["tratamentos"]) => {
    setTratamentos((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const onSubmit = async (
    data: Omit<
      IDeclaration,
      "tratamentos" | "assinatura_paciente" | "assinatura_dentista"
    >
  ) => {
    if (!signaturePatient) {
      toast.error("Preencha a assinatura do paciente");
      return false;
    }
    if (!signatureDoctor) {
      toast.error("Preencha a assinatura do dentista");
      return false;
    }

    const fichaCompleta: IDeclaration = {
      ...data,
      tratamentos,
      assinatura_paciente: signaturePatient,
      assinatura_dentista: signatureDoctor,
      paciente_id: pacId,
      numero_cadastro: pacFantasy,
    };

    try {
      setLoading(true);
      const { status, data: dataForm } = await sendDeclaration(
        fichaCompleta,
        user.data.id
      );

      if ([200, 201].includes(status)) {
        toast.success("Formulário enviado com sucesso");
        setIdForm(dataForm.data.NR_SEQ_CONTRATOFORM_CFOC as number);
        setLoading(false);
        setShowPostModal(true);
        onCompleteOrto?.();
      } else {
        setLoading(false);
        toast.error("Erro ao enviar formulário");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onDownload = async () => {
    try {
      const { status, data } = await pdfDownload(
        { paciente_id: pacId, form_type: "declaracao_tratamentos" },
        user.data.id
      );

      if ([200, 201].includes(status)) {
        const base64 = data.data;

        //@ts-ignore
        if (window && window.ReactNativeWebView) {
          //call rn to download
          //@ts-ignore
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              action: "downloadBase64File",
              value: base64,
            })
          );
        } else {
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "declaracao_tratamentos.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        toast.success("PDF baixado com sucesso");
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao baixar o PDF");
    }
  };

  /*const clear = () => {
    sigCanvasDentist.current?.clear();
    setSignatureDoctor(null);
    sigCanvasPatient.current?.clear();
    setSignaturePatient(null);
    reset();
    setTratamentos({
      periodontal: false,
      protetico: false,
      implantes: false,
      restaurador: false,
      cirurgico: false,
      estetico: false,
      outros: false,
    });
  };*/

  return (
    <Modal open={open} onClose={() => close(false)}>
      <Box sx={{ backgroundColor: "white", height: "100%", width: "100%" }}>
        <Container maxWidth="xl" sx={{ overflow: "scroll", height: "100%" }}>
          <ReturnButton
            title="Declaração de Tratamentos Complementares"
            onClick={close}
          />
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="responsavel"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Responsável (Caso necessário)"
                  fullWidth
                  margin="normal"
                  error={!!errors.responsavel}
                  helperText={errors.responsavel?.message}
                />
              )}
            />

            <Controller
              name="rg"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  InputProps={{
                    inputComponent: IMaskInput as any,
                    inputProps: {
                      mask: "00.000.000-0",
                    },
                  }}
                  label="RG do Responsável (Caso necessário)"
                  fullWidth
                  margin="normal"
                  error={!!errors.rg}
                  helperText={errors.rg?.message}
                />
              )}
            />

            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  InputProps={{
                    inputComponent: IMaskInput as any,
                    inputProps: {
                      mask: "000.000.000-00",
                    },
                  }}
                  label="CPF do Responsável (Caso necessário)"
                  fullWidth
                  margin="normal"
                  error={!!errors.cpf}
                  helperText={errors.cpf?.message}
                />
              )}
            />

            <Box mt={2}>
              <FormLabel component="legend">
                Declaro para os devidos fins de direito ter pleno conhecimento
                de que o problema pelo qual procurei tratamento ortodôntico, não
                poderá ser corrigido de forma integral apenas com o tratamento
                ortodôntico, sendo necessário também:
              </FormLabel>
              <Grid container spacing={2}>
                {[
                  { key: "periodontal", label: "Periodontal" },
                  { key: "protetico", label: "Protético" },
                  { key: "implantes", label: "Implantes" },
                  { key: "restaurador", label: "Restaurador" },
                  { key: "cirurgico", label: "Cirúrgico" },
                  { key: "estetico", label: "Estético / Cosmético" },
                  { key: "outros", label: "Outros" },
                ].map(({ key, label }) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={tratamentos[key as keyof typeof tratamentos]}
                          onChange={() =>
                            handleTratamentoChange(
                              key as keyof typeof tratamentos
                            )
                          }
                        />
                      }
                      label={label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {tratamentos.outros && (
              <Controller
                name="outros"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Descreva o tratamento 'Outros'"
                    fullWidth
                    margin="normal"
                    error={!!errors.outros}
                    helperText={errors.outros?.message}
                  />
                )}
              />
            )}

            <Controller
              name="observacao"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Observação"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                  error={!!errors.observacao}
                  helperText={errors.observacao?.message}
                />
              )}
            />

            <Styled.BoxClinicalSignature className="boxValue">
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                Assinatura do Paciente ou Responsável
                <Button onClick={clearSignaturePatient} className="no-print">
                  Limpar
                </Button>
              </Typography>
              <div className="boxSignature" ref={sigBoxRef}>
                {canvasWidth > 0 && (
                  <SignatureCanvas
                    onEnd={saveSignaturePatient}
                    ref={sigCanvasPatient}
                    penColor="black"
                    canvasProps={{
                      height: 120,
                      className: "sigCanvas",
                      width: canvasWidth,
                    }}
                  />
                )}
              </div>
            </Styled.BoxClinicalSignature>

            <Styled.BoxClinicalSignature className="boxValue">
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                Assinatura do Dentista
                <Button onClick={clearSignatureDentits} className="no-print">
                  Limpar
                </Button>
              </Typography>
              <div className="boxSignature">
                {canvasWidth > 0 && (
                  <SignatureCanvas
                    onEnd={saveSignatureDentist}
                    ref={sigCanvasDentist}
                    penColor="black"
                    canvasProps={{
                      height: 120,
                      className: "sigCanvas",
                      width: canvasWidth,
                    }}
                  />
                )}
              </div>
            </Styled.BoxClinicalSignature>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2, mb: 2 }}
            >
              {loading ? <CircularProgress color="inherit" /> : "Enviar"}
            </Button>
          </Box>
        </Container>

        <PostSubmitModal
          open={showPostModal}
          onClose={() => {
            setShowPostModal(false);
            //clear();
            close(false);
          }}
          onDownload={onDownload}
          onSendWhatsApp={() => {
            toast.success("Link enviado no WhatsApp");
          }}
          defaultEmail={emailPatient as string}
          formId={idForm}
          userId={user.data.id}
          userWpp={""}
        />
      </Box>
    </Modal>
  );
};

export { DeclarationReferrals };
